import React, { useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Button,
    Paper,
    CircularProgress,
} from '@mui/material';
import { IPledgeStatus } from "../dashboard/components/PledgeStatus";
import Api, { APIs } from "../../Api";
import CommonUtils from "../base/utils/CommonUtils";

// Define AlertType
export const AlertType = Object.freeze({
    none: "",
    info: "info",
    error: "error",
    warning: "warning",
    success: "success",
});

const PledgeStatusEditing: React.FC = () => {
    const [tableData, setTableData] = useState<IPledgeStatus[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [savingRow, setSavingRow] = useState<number | null>(null);

    useEffect(() => {
        Api.get(APIs.MoPD_PLEDGE_STATUS)
            .then((payload) => {
                if (payload) {
                    setTableData(payload.data as IPledgeStatus[]);
                }
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
                CommonUtils.showSnackbar("Failed to fetch data", AlertType.error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        rowIndex: number,
        field: keyof IPledgeStatus
    ) => {
        const newData = [...tableData];
        const currentRow = newData[rowIndex];

        if (field === "disbursement_date" || field === "projection_date") {
            currentRow[field] = e.target.value; // Save as string
        } else {
            currentRow[field] = parseFloat(e.target.value) || 0;
        }

        setTableData(newData);
    };


    const handleSave = (row: IPledgeStatus) => {
        setSavingRow(row.id);
        Api.post(APIs.MoPD_PLEDGE_STATUS_EDITING, row)
            .then((response) => {
                console.log('Updated row:', response.data);
                CommonUtils.showSnackbar("Row updated successfully", AlertType.success);
            })
            .catch((error) => {
                console.error('Error updating row:', error);
                CommonUtils.showSnackbar("Failed to update row", AlertType.error);
            })
            .finally(() => {
                setSavingRow(null);
            });
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <CircularProgress />
            </div>
        );
    }

    const headerCellStyle = {
        backgroundColor: 'black',
        color: 'white',
        fontSize: '1.1rem',
    };

    const maxHeight = CommonUtils.getPaperHeight(10, 0);

    return (
        <TableContainer component={Paper} style={{ maxHeight: maxHeight, overflowY: 'auto' }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell style={headerCellStyle}>ID</TableCell>
                        <TableCell style={headerCellStyle}>Development Partner</TableCell>
                        <TableCell style={headerCellStyle}>Pledged Amount (US$ Mn)</TableCell>
                        <TableCell style={headerCellStyle}>Amount Released (US$ Mn)</TableCell>
                        <TableCell style={headerCellStyle}>Disbursement</TableCell>
                        <TableCell style={headerCellStyle}>Disbursement Date</TableCell>
                        <TableCell style={headerCellStyle}>Projected Disbursement</TableCell>
                        <TableCell style={headerCellStyle}>Projected Date</TableCell>
                        <TableCell style={headerCellStyle}>Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tableData.map((row, rowIndex) => (
                        <TableRow key={row.id}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.development_partner}</TableCell>
                            <TableCell>
                                {row.pledged_amount_million_usd?.toLocaleString() || 'N/A'}
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    value={row.amount_released_million_usd || ''}
                                    onChange={(e) => handleInputChange(e, rowIndex, 'amount_released_million_usd')}
                                    variant="outlined"
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    value={row.disbursement || ''}
                                    onChange={(e) => handleInputChange(e, rowIndex, 'disbursement')}
                                    variant="outlined"
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="date"
                                    value={typeof row.disbursement_date === 'string' ? row?.disbursement_date?.split('T')[0] : ''}
                                    onChange={(e) => handleInputChange(e, rowIndex, 'disbursement_date')}
                                    variant="outlined"
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    value={row.projected_disbursement || ''}
                                    onChange={(e) => handleInputChange(e, rowIndex, 'projected_disbursement')}
                                    variant="outlined"
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="date"
                                    value={row.projection_date ? row.projection_date.split('T')[0] : ''}
                                    onChange={(e) => handleInputChange(e, rowIndex, 'projection_date')}
                                    variant="outlined"
                                    size="small"
                                />
                            </TableCell>
                            <TableCell>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleSave(row)}
                                    disabled={savingRow === row.id}
                                >
                                    {savingRow === row.id ? <CircularProgress size={24} color="inherit" /> : "Save"}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PledgeStatusEditing;
